import React, { useCallback, useEffect, useState } from "react";
import { DollarSign, Users, TrendingDown, AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const Stat = ({ label, value, tone = "foreground", Icon, testId }) => (
    <div className="card-soft p-4" data-testid={testId}>
        <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {Icon && <Icon size={11} />} {label}
        </div>
        <div className={`mt-1 font-heading font-bold text-2xl mono-score text-${tone}`}>{value}</div>
    </div>
);

export default function AdminBilling() {
    const [sum, setSum] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [{ data: s }, { data: e }] = await Promise.all([
                api.get("/admin/billing/summary"),
                api.get("/admin/billing/recent-events?limit=15"),
            ]);
            setSum(s);
            setEvents(e || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="admin-billing-page">
            <div className="mb-6">
                <p className="overline">Command Center · Billing</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Billing Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    MRR, active subscribers, churn, and recent Stripe events. {sum?.stripe_live ? null : <span className="text-amber-600">Stripe not fully configured — showing local webhook cache only.</span>}
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Stat label="MRR" value={sum?.mrr_display || "$0.00"} tone="emerald-600" Icon={DollarSign} testId="stat-mrr" />
                        <Stat label="Active subs" value={sum?.active_subscribers ?? 0} Icon={Users} testId="stat-active" />
                        <Stat label="Churn · 30d" value={sum?.churn_30d ?? 0} tone="amber-600" Icon={TrendingDown} testId="stat-churn" />
                        <Stat label="Failed · 30d" value={sum?.failed_payments_30d ?? 0} tone="destructive" Icon={AlertTriangle} testId="stat-failed" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="card-soft p-4">
                            <div className="overline mb-3">Subscribers by plan</div>
                            {(sum?.by_plan || []).length === 0 ? (
                                <div className="text-xs text-muted-foreground">No active subscribers yet.</div>
                            ) : (
                                <div className="space-y-2">
                                    {(sum?.by_plan || []).map((p) => (
                                        <div key={p.plan_key} className="flex items-center justify-between text-sm">
                                            <span className="text-foreground truncate">{p.plan_key}</span>
                                            <Badge className="border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11px]">{p.count}</Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="card-soft p-4">
                            <div className="overline mb-3">Recent Stripe events</div>
                            {events.length === 0 ? (
                                <div className="text-xs text-muted-foreground">No events yet.</div>
                            ) : (
                                <div className="space-y-1">
                                    {events.map((e) => (
                                        <div key={e.event_id} className="flex items-center justify-between text-xs">
                                            <span className="mono-score text-foreground truncate">{e.type}</span>
                                            <span className="text-muted-foreground whitespace-nowrap ml-2">{e.received_at ? new Date(e.received_at).toLocaleString() : "—"}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
